import { useEffect, useRef, useMemo } from 'react';
import { useLocation, NavLink, Outlet } from 'react-router-dom';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  IconExpand,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  Filters,
  IconSelected,
  IconBlock,
  IconShow,
  usePrevious,
  useGetList,
  PermissionEnum,
  SchemaFilterType,
  SortDir,
  formatSearchParams,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearBanksState } from './store/slices/list';
import { PspPartnerItem } from './types';
import {
  bankAndPspListColumns,
  bankAndPspListSchema,
  bankAndPspListFiltersSchema,
  bankAndPspListColumnsLabels,
} from './variables/list';
import { useLazyGetConfigurationPspPartnersQuery } from './store/services';
import { clearBankAndPspInfoState } from './store/slices/info';
import { PaymentType } from '../../store/dictionaries/types';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const formatBankAndPspList = (
  list: PspPartnerItem[],
  paymentTypes?: PaymentType[],
): PspPartnerItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const filteredPaymentTypes: PaymentType[] = [];

    paymentTypes?.forEach((paymentType) => {
      if (
        result?.paymentsTypes?.find((type) => paymentType.paymentType === type)
      ) {
        filteredPaymentTypes.push(paymentType);
      }
    });

    const render: Record<string, React.JSX.Element> = {
      companyName: <div>{item.companyName}</div>,
      active: (
        <div>
          {item.active ? (
            <IconSelected className={cn.bankAndPspItemIconActive} />
          ) : (
            <IconBlock className={cn.bankAndPspItemIconDisabled} />
          )}
        </div>
      ),
      paymentsTypes: (
        <div className={cn.bankAndPspPaymentTypesWrapper}>
          <div className={cn.bankAndPspPaymentTypes}>
            {filteredPaymentTypes.map((paymentType) => (
              <div
                className={cn.bankAndPspPaymentType}
                key={paymentType.paymentType}
              >
                {paymentType.name}
              </div>
            ))}
          </div>

          <span>
            Show
            <IconShow width={16} height={16} />
          </span>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const BankAndPspList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    list: { values, pagination },
    info: { generalInfo },
  } = useAppSelector((state) => state.bankAndPsp);

  const { activePermissions, paymentTypes } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(bankAndPspListSchema);

  const prevFilters = usePrevious(filters);
  const prevPagination = usePrevious(pagination);

  const formattedData = useMemo(() => {
    const data = formatBankAndPspList(values as PspPartnerItem[], paymentTypes);

    return data;
  }, [values, sort, filters, paymentTypes]);

  const [getList, { isFetching, isLoading }] =
    useLazyGetConfigurationPspPartnersQuery();

  const { getListAction, onFilter, onSort, setPage, errorList } = useGetList({
    getList,
    onFilterChange,
    onSortChange,
    schema: bankAndPspListSchema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: bankAndPspListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    dispatch(clearBankAndPspInfoState());
  }, [generalInfo]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      sort &&
      sort.sortProp
    ) {
      const parsedQuery = queryString.parse(search || '');
      const parsedQueryWithoutSort: Record<
        string,
        string | (string | null)[] | null
      > = {};

      let currentFilters = filters;
      const currentFiltersStorage = localStorage.getItem('banksFilters');

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        onFilter({
          ...currentFilters,
        });
      } else {
        Object.entries(parsedQuery).forEach(([key, filterValue]) => {
          if (key !== 'sortProp' && key !== 'sortDir') {
            parsedQueryWithoutSort[key] = filterValue;
          }
        });

        if (
          !currentFiltersStorage &&
          !Object.keys(parsedQueryWithoutSort).length
        ) {
          const filtersValues: Filters = {
            ...currentFilters,
            sortDir: {
              value: [sort?.sortDir as string],
              type: SchemaFilterType.STRING,
            },
            sortProp: {
              value: [sort?.sortProp],
              type: SchemaFilterType.STRING,
            },
          };

          onFilter({
            ...filtersValues,
          });
        } else if (!values) {
          getListAction({
            filters: {
              ...currentFilters,
            },
            sort,
          });
        }
      }
    }
  }, [filters, sort, prevFilters, values, search]);

  useEffect(() => {
    if (!values && search) {
      formatSearchParams({
        currentSchemaValues: bankAndPspListSchema,
        storageKey: 'banksFilters',
        search,
      });
    }
  }, [values, search]);

  useEffect(() => {
    dispatch(clearBanksState());
  }, []);

  useEffect(() => {
    const currentFilters = localStorage.getItem('banksFilters');

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'banksFilters',
          JSON.stringify({
            ...parsed,
          }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    let title = 'Banks';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.bankAndPspLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        title="Banks"
        columns={bankAndPspListColumns}
        data={formattedData || []}
        onSort={(name) => {
          onSort(name);

          const nextSortDirt =
            sort?.sortDir === SortDir.DESC ? SortDir.ASC : SortDir.DESC;

          localStorage.setItem(
            'banksFilters',
            JSON.stringify({
              ...filters,
              sortProp: {
                value: [name || 'companyName'],
                type: SchemaFilterType.STRING,
              },
              sortDir: {
                value: [nextSortDirt || SortDir.DESC],
                type: SchemaFilterType.STRING,
              },
            }),
          );
        }}
        filters={filters}
        sort={sort}
        columnsLabels={bankAndPspListColumnsLabels}
        onFilterChange={(filterValues) => {
          onFilter(filterValues);

          localStorage.setItem(
            'banksFilters',
            JSON.stringify({
              ...filterValues,
              sortProp: {
                value: [sort.sortProp || 'companyName'],
                type: SchemaFilterType.STRING,
              },
              sortDir: {
                value: [sort.sortDir || SortDir.DESC],
                type: SchemaFilterType.STRING,
              },
            }),
          );
        }}
        error={errorList}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        pagination={pagination}
        take="20"
        setPage={(data) => {
          setPage({
            ...data,
            filters: {
              ...filters,
              sortProp: {
                value: [sort.sortProp || 'companyName'],
                type: SchemaFilterType.STRING,
              },
              sortDir: {
                value: [sort.sortDir || SortDir.DESC],
                type: SchemaFilterType.STRING,
              },
            },
          });

          localStorage.setItem(
            'banksFilters',
            JSON.stringify({
              ...filters,
              cursor: {
                value: [data.id],
                type: SchemaFilterType.STRING,
              },
              sortProp: {
                value: [sort.sortProp || 'companyName'],
                type: SchemaFilterType.STRING,
              },
              sortDir: {
                value: [sort.sortDir || SortDir.DESC],
                type: SchemaFilterType.STRING,
              },
            }),
          );
        }}
        topContent={
          activePermissions?.includes(PermissionEnum.PSP_ADD_ACTION) ? (
            <NavLink
              to={`${BankRoutes.BANK_AND_PSP_CREATE}${search || ''}`}
              className={cn.bankAndPspCreateLink}
            >
              <div className={cn.bankAndPspCreateLinkContent}>
                <IconExpand className={cn.bankAndPspCreateLinkIcon} />
                <span>Add Bank</span>
              </div>
            </NavLink>
          ) : undefined
        }
        clickable
      />
    );
  }

  return (
    <div className={cn.bankAndPspBottom} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default BankAndPspList;
